import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Footer } from "@/components/hirekit/Footer";

const API_URL = (import.meta.env["VITE_API_URL"] as string | undefined)?.replace(/\/$/, "") ?? "";

/** Human label for a form control: <label for>, wrapping label, fieldset legend, aria-label, placeholder. */
function labelFor(el: HTMLElement, form: HTMLFormElement): string {
  const id = el.id;
  const byFor = id ? form.querySelector(`label[for="${id}"]`) : null;
  const wrap = el.closest("label");
  const legend = el.closest("fieldset")?.querySelector("legend");
  return (
    byFor?.textContent ||
    (el.tagName !== "INPUT" || (el as HTMLInputElement).type !== "radio"
      ? wrap?.textContent
      : "") ||
    legend?.textContent ||
    el.getAttribute("aria-label") ||
    el.getAttribute("placeholder") ||
    ""
  )
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Collects a static lead form (the design's forms have no handler of their own)
 * and sends it through the existing public contact endpoint.
 */
async function submitLeadForm(form: HTMLFormElement): Promise<void> {
  const lines: string[] = [];
  let name = "";
  let email = "";
  let company = "";
  for (const el of Array.from(form.elements) as (
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  )[]) {
    if (!el.tagName || el.tagName === "BUTTON" || el.tagName === "FIELDSET") continue;
    const input = el as HTMLInputElement;
    if (input.type === "file" || input.type === "submit") continue;
    let value = "";
    if (input.type === "radio") {
      if (!input.checked) continue;
      value = input.closest("label")?.textContent?.replace(/\s+/g, " ").trim() ?? input.value;
    } else if (input.type === "checkbox") {
      if (!input.checked) continue;
      value = "Yes";
    } else value = el.value.trim();
    if (!value) continue;
    const label = labelFor(el, form) || el.name || "Field";
    if (!name && /name/i.test(label) && !/company/i.test(label)) name = value;
    else if (!email && /e-?mail/i.test(label)) email = value;
    else if (!company && /company/i.test(label)) company = value;
    else lines.push(`${label}: ${value}`);
  }
  const res = await fetch(`${API_URL}/api/public/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: name || "Website enquiry",
      email,
      company,
      message: `[${document.title}]\n${lines.join("\n") || "No further details."}`,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.messages?.error ?? body?.error ?? "Something went wrong. Try again.");
  }
}

/**
 * Host for markup ported 1:1 from the design artifact. Tailwind's preflight is
 * undone inside .dc (see styles.css) so the inline styles render exactly as
 * designed, cross-page #anchors scroll into view after a client render, and
 * any form the page doesn't handle itself is sent as an enquiry.
 */
export function DcPage({ css, children }: { css: string; children: ReactNode }) {
  const [note, setNote] = useState<{ text: string; bad: boolean } | null>(null);

  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    let tries = 0;
    const go = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start" });
      else if (++tries < 20) setTimeout(go, 50);
    };
    setTimeout(go, 0);
  }, []);

  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 6000);
    return () => clearTimeout(t);
  }, [note]);

  async function onSubmit(e: FormEvent<HTMLDivElement>) {
    if (e.defaultPrevented) return; // a page-level handler already took it
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    e.preventDefault();
    try {
      await submitLeadForm(form);
      form.reset();
      setNote({ text: "Thanks, we have your request and will be in touch shortly.", bad: false });
    } catch (err) {
      setNote({
        text: err instanceof Error ? err.message : "Something went wrong. Try again.",
        bad: true,
      });
    }
  }

  return (
    <div className="dc" onSubmit={onSubmit}>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <style>{".dc #top > footer { display: none; }"}</style>
      {children}
      <Footer />
      {note ? (
        <p
          role="status"
          style={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#FFFFFF",
            color: note.bad ? "#B3261E" : "#0B2160",
            border: "1px solid #E3E3DE",
            borderRadius: 8,
            padding: "12px 18px",
            fontFamily: "Archivo, system-ui, sans-serif",
            fontWeight: 700,
            zIndex: 50,
            maxWidth: "90vw",
            margin: 0,
          }}
        >
          {note.text}
        </p>
      ) : null}
    </div>
  );
}
